import styled from "@emotion/styled";
import { useLocation } from "react-router-dom";
import { User } from "features/auth";
import { useArtist } from "features/reviewable";
import { Text, H2, Link, Stack, AllCaps, FlexRow, BrushWrapper } from "atoms";
import { trackEvent } from "contexts/mixpanel";
import { useFollows } from "../api/useFollows";
import { FollowButton } from "./FollowButton";

const trackSignIn = () =>
  trackEvent("Button Click", {
    ButtonName: "Sign In",
    ButtonContext: "Follow Invite Banner",
    Location: window.location.href,
  });
const trackSignUp = () =>
  trackEvent("Button Click", {
    ButtonName: "Sign Up",
    ButtonContext: "Follow Invite Banner",
    Location: window.location.href,
  });

type FollowInviteBannerprops = {
  artistId: string;
  user: User | undefined;
};

function FollowInviteBanner({ artistId, user }: FollowInviteBannerprops) {
  const { data: artist } = useArtist(artistId);
  const { data: follows } = useFollows();
  const location = useLocation();

  if (!artistId || !artist) return null;

  const userFollowsArtist = follows?.artistIds.includes(artist.id);

  if (!user)
    return (
      <InviteBanner>
        <Stack gap="sm">
          <H2>You&apos;re invited to follow {artist.name}</H2>
          <Text>
            Never miss a drop again! {artist.name} invited you to follow them on{" "}
            <AllCaps>Hug</AllCaps> to get the latest drops,
            announcements,&nbsp;and&nbsp;more!
          </Text>
          <FlexRow justifyContent="center" gap="10px">
            <Link
              to="/sign-in"
              state={{ from: location }}
              variant="secondary"
              size="sm"
              onClick={trackSignIn}
            >
              Sign in to follow
            </Link>
            <Link
              to={`/register?artistId=${artist.id}`}
              state={{ from: location }}
              variant="secondary"
              size="sm"
              onClick={trackSignUp}
            >
              Create an Account
            </Link>
          </FlexRow>
        </Stack>
      </InviteBanner>
    );

  if (userFollowsArtist)
    return (
      <InviteBanner>
        <Stack gap="sm">
          <H2>You&apos;re following {artist.name}</H2>
          <Text>
            Following artists on <AllCaps>Hug</AllCaps> is the best way to stay
            up to date on theirlatest drops, announcements,&nbsp;and&nbsp;more!
          </Text>
        </Stack>
      </InviteBanner>
    );

  return (
    <InviteBanner>
      <Stack gap="sm">
        <H2>You&apos;re invited to follow {artist.name}</H2>
        <Text>
          Never miss a drop again! {artist.name} has invited you to follow them
          on HUG to get the latest drops, announcements,&nbsp;and&nbsp;more!
        </Text>
        <FlexRow justifyContent="center">
          <FollowButton entityId={artist.id} variant="secondary" />
        </FlexRow>
      </Stack>
    </InviteBanner>
  );
}

const InviteBanner = styled(BrushWrapper)(({ theme }) => ({
  textAlign: "center",
  padding: `calc(2rem + 1vw) ${theme.spacing.gutter}`,
  "a, button": {
    color: theme.colors.fg,
    "&:hover, &:focus-visible": {
      backgroundColor: theme.colors.fg80,
      color: theme.colors.bg,
    },
  },
  p: {
    maxWidth: 660,
    marginInline: "auto",
  },
}));
InviteBanner.defaultProps = {
  bg: "fg",
};

export { FollowInviteBanner };
